import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Gift } from '@clds/icon';
import { useAnalytics } from '@cld/analytics';
import type { CloseTrigger } from '@cld/share-with-a-friend-modal';
import ShareWithAFriendModal from '@cld/share-with-a-friend-modal';
import { messages } from '../../i18n';
import { MENU_ITEM_ICON_SIZE } from '../../ApplicationWrapper.const';
import { PrimaryMenuItem } from '../PrimaryMenu/PrimaryMenuItem';
import type { SocialInvitation } from '../../api/socialInvitation/fetchSocialInvitation';
import { useCustomerIdContext } from '../../context';

interface InviteFriendProps {
  fetchSocialInvitation: (customerId: string) => Promise<SocialInvitation>;
}

export const InviteAFriend: FC<InviteFriendProps> = ({ fetchSocialInvitation }) => {
  const { formatMessage } = useIntl();
  const { currentCustomer } = useCustomerIdContext();
  const analytics = useAnalytics('primary menu');
  const [isOpen, setOpen] = useState(false);
  const [socialInvitation, setSocialInvitation] = useState<SocialInvitation | null>(null);

  const toggleIsOpen = () => setOpen((prvState) => !prvState);

  const customerId = currentCustomer?.id;
  const externalCustomerId = currentCustomer?.externalId;

  useEffect(() => {
    if (externalCustomerId && customerId) {
      void fetchSocialInvitation(customerId).then(setSocialInvitation);
    }
  }, [customerId, externalCustomerId, fetchSocialInvitation]);

  return (
    <>
      <PrimaryMenuItem
        analyticsMessage="invite a friend"
        analyticsAction="open modal"
        data-test-specifier="share with a friend button"
        onClick={toggleIsOpen}
        tooltipTitle={formatMessage(messages.referFriends)}
      >
        <Gift data-testid="invite-a-friend-icon" size={MENU_ITEM_ICON_SIZE} />
      </PrimaryMenuItem>
      {socialInvitation && (
        <ShareWithAFriendModal
          open={isOpen}
          onClose={
            /* istanbul ignore next */ (reason: CloseTrigger) => {
              toggleIsOpen();
              analytics(`invite a friend modal | close using ${reason === 'x button' ? 'x' : 'close'} button`, 'close');
            }
          }
          onCopy={
            /* istanbul ignore next */ () => {
              analytics('copy to clipboard', 'invite a friend modal | copy invite link');
            }
          }
          title={formatMessage(messages.shareWithAFriendModalTitle)}
          content={formatMessage(messages.shareWithAFriendModalContent)}
          note={formatMessage(messages.shareWithAFriendModalNote)}
          link={socialInvitation.cloudinary.url}
        />
      )}
    </>
  );
};
